import React, { useEffect, useState } from "react";

import { trackAmplitudeEvent } from "../../amplitude";
import Loading from "../Loading";
import Modal from "../Modal";
import ModalFooterCloseBtn from "../ModalFooterCloseBtn";
import ModalProPromo from "../ModalProPromo";
import NewClozeTextSheet from "./NewClozeTextSheet";
import StoryFiltersSheet from "./StoryFiltersSheet";
import StoryRow from "./StoryRow";
import WikipediaProgress from "./WikipediaProgress";
import { STORIES_EVENTS } from "./storiesEvents";
import { goTo, requestJson } from "./storiesHelpers";
import {
  clozeTextToStoryListItem,
  compInputToStoryListItem,
  countActiveFilters,
  DEFAULT_STORY_FILTERS,
  DEFAULT_STORY_SORT,
  filterStories,
  getStoryPath,
  getTopicsWithStories,
  getWikipediaProgress,
  pluralizeTexts,
  sortStories,
  STORY_SOURCES,
  toggleFilter
} from "./storyLibrary";
import { getStoryOpenedTimes, saveStoryOpened } from "./storyStorage";

const loadList = async (url) => {
  if (!url) return {};
  try {
    return await requestJson({ url });
  } catch {
    return { hasError: true };
  }
};

export default function StoriesLibrary({ clozeTextsUrl, compInputsUrl, isPro, languagePairingSlug, storiesPath, userClozeTextsUrl }) {
  const [items, setItems] = useState(null);
  const [wikipediaProgress, setWikipediaProgress] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_STORY_FILTERS);
  const [sort, setSort] = useState(DEFAULT_STORY_SORT);
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [proPromo, setProPromo] = useState(null);
  const [isNewClozeTextVisible, setIsNewClozeTextVisible] = useState(false);
  const trackingProperties = { language_pairing_slug: languagePairingSlug };

  const loadStories = async () => {
    setHasError(false);
    const [compInputs, clozeTexts] = await Promise.all([loadList(compInputsUrl), loadList(clozeTextsUrl)]);
    const openedTimes = getStoryOpenedTimes();
    const loadedItems = [
      ...(compInputs.compInputs ?? []).map((compInput) => compInputToStoryListItem(compInput, openedTimes)),
      ...(clozeTexts.clozeTexts ?? []).map((clozeText) => clozeTextToStoryListItem(clozeText, openedTimes)),
      ...(clozeTexts.myClozeTexts ?? []).map((clozeText) => clozeTextToStoryListItem(clozeText, openedTimes, "mine"))
    ];
    if (!loadedItems.length && (compInputs.hasError || clozeTexts.hasError)) {
      setHasError(true);
      return;
    }
    setWikipediaProgress(clozeTexts.clozeTexts?.length ? getWikipediaProgress(clozeTexts.clozeTexts, clozeTexts.sentenceCount) : null);
    setItems(loadedItems);
  };

  useEffect(() => {
    trackAmplitudeEvent(STORIES_EVENTS.STORIES_OPENED, trackingProperties);
    loadStories();
  }, []);

  const changeFilter = (group, value) => {
    const nextFilters = toggleFilter(filters, group, value);
    if (nextFilters === filters) return;
    trackAmplitudeEvent(STORIES_EVENTS.FILTER_CHANGED, { ...trackingProperties, filter: group, selected: nextFilters[group].includes(value), value });
    if (group === "levels" && nextFilters.levels.includes(value)) {
      trackAmplitudeEvent(STORIES_EVENTS.LEVEL_SELECTED, { ...trackingProperties, cefr_levels: [value], level: value });
    }
    setFilters(nextFilters);
  };

  const clearFilters = () => {
    trackAmplitudeEvent(STORIES_EVENTS.FILTERS_CLEARED, trackingProperties);
    setFilters(DEFAULT_STORY_FILTERS);
    setSort(DEFAULT_STORY_SORT);
  };

  const changeSort = (nextSort) => {
    trackAmplitudeEvent(STORIES_EVENTS.SORT_CHANGED, { ...trackingProperties, sort: nextSort });
    setSort(nextSort);
  };

  const isLockedForUser = (item) => !item.isFree && !isPro;

  const openStory = (item) => {
    if (isLockedForUser(item)) {
      setProPromo("read every Wikipedia article and your own cloze-texts in Stories.");
      return;
    }
    if (item.isReady === false) {
      goTo(`${userClozeTextsUrl}/${item.slug}/status`);
      return;
    }
    saveStoryOpened(item.key);
    goTo(getStoryPath(storiesPath, item));
  };

  const openNewClozeText = () => {
    if (!isPro) {
      setProPromo("create their own cloze-texts from any text.");
      return;
    }
    setIsNewClozeTextVisible(true);
  };

  if (hasError) {
    return (
      <div className="stories-message">
        <p>Oh no! There was an error loading texts. Please try again.</p>
        <button className="btn btn-success joystix" type="button" onClick={loadStories}>Retry</button>
      </div>
    );
  }

  if (!items) {
    return <Loading />;
  }

  const visibleItems = sortStories(filterStories(items, filters), sort);
  const sourceCounts = Object.fromEntries(STORY_SOURCES.map((source) => [source.id, items.filter((item) => item.source === source.id).length]));
  const activeFilterCount = countActiveFilters(filters);

  return (
    <div className="stories-library">
      <div className="stories-toolbar">
        <button className="stories-toolbar-button" data-testid="stories-filters-button" type="button" onClick={() => setIsFiltersVisible(true)}>
          <span className="fa fa-sliders" />
          Filters
          {activeFilterCount > 0 && <span className="stories-toolbar-badge" data-testid="stories-filters-count">{activeFilterCount}</span>}
        </button>
        {!!userClozeTextsUrl && (
          <button className="stories-toolbar-button" data-testid="stories-new-cloze-text" type="button" onClick={openNewClozeText}>
            <span className="fa fa-plus" />
            New cloze-text
          </button>
        )}
      </div>
      {!!wikipediaProgress && <WikipediaProgress progress={wikipediaProgress} />}
      {items.length ? (
        <>
          <p className="stories-count" data-testid="stories-count">{pluralizeTexts(visibleItems.length)}</p>
          {visibleItems.length ? (
            <div className="story-rows">
              {visibleItems.map((item) => <StoryRow isLocked={isLockedForUser(item)} item={item} key={item.key} onOpen={openStory} />)}
            </div>
          ) : (
            <div className="stories-message" data-testid="stories-no-matches">
              <p className="text-muted">No texts match these filters.</p>
              <button className="btn btn-success joystix" data-testid="stories-clear-filters" type="button" onClick={clearFilters}>Clear filters</button>
            </div>
          )}
        </>
      ) : (
        <p className="stories-message text-muted">No texts available for this language yet. Check back soon!</p>
      )}
      {isFiltersVisible && (
        <StoryFiltersSheet
          filters={filters}
          sort={sort}
          sourceCounts={sourceCounts}
          topics={getTopicsWithStories(items.filter((item) => filters.sources.includes(item.source)))}
          visibleCount={visibleItems.length}
          onClear={clearFilters}
          onClose={() => setIsFiltersVisible(false)}
          onSort={changeSort}
          onToggle={changeFilter}
        />
      )}
      {isNewClozeTextVisible && <NewClozeTextSheet userClozeTextsUrl={userClozeTextsUrl} onClose={() => setIsNewClozeTextVisible(false)} />}
      {!!proPromo && (
        <Modal footer={<ModalFooterCloseBtn />} title="Pro Feature" onHidden={() => setProPromo(null)}>
          <ModalProPromo can={proPromo} />
        </Modal>
      )}
    </div>
  );
}
